import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AuthenticationService, ConfigurationService, LocalStorageService, RestApiService } from '@draeger-academy/common';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { OrderService } from '@draeger-academy/common/lib/services/order/order.service';
import { TranslateService } from '@ngx-translate/core';
import { Order } from 'models/order';
import { Participant } from 'models/participant';
import { WINDOWS_OS } from '@viablelogic/ngx-responsive';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { GenericModalService } from '../../generic-modal';
import { environment } from 'src/environments/environment';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Router } from '@angular/router';

@Component({
    selector: 'da-booking-participants',
    templateUrl: './booking-participants.component.html',
    styleUrls: ['./booking-participants.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingParticipantsComponent implements OnInit {
    @Input() activeStep: number;
    public booking: UntypedFormGroup;
    @Output()
    nextStep: EventEmitter<string> = new EventEmitter<string>();
    eventsSubject: Subject<void> = new Subject<void>();
    public order: Order = new Order([]);
    public bookingPerson = '';
    public showErrorMessage = false;
    public showTotalPriceMessage = false;
    public persons = [{}];
    public emptyPerson;
    public currency: string;
    public format = '';
    public languageCode = '';
    private _subscription: Subscription;
    private _order: Subscription;
    private _modalId: string;
    private _currentData = null;
    public choice1 = true;
    public choice2 = true;
    public isGroupBooking = false;
    public isParticipants = true;
    public screeningObject: any;

    @ViewChild('overlay', { static: true }) private _overlay;
    private _$location: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private _$user: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$totalPrice: BehaviorSubject<any> = new BehaviorSubject([]);
    private _$showTotalPriceMessage: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$order: BehaviorSubject<any> = new BehaviorSubject([]);
    private _$employees: BehaviorSubject<any> = new BehaviorSubject([]);
    private _$currencyPositionBefore: BehaviorSubject<string> = new BehaviorSubject('');
    private _$errorMessage: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showChoice: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$isGroupBooking: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$addedParticipants: BehaviorSubject<Array<Participant>> = new BehaviorSubject(Array<Participant>());

    @ViewChild('goupEmployees', { static: true }) private _goupEmployees;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private orderService: OrderService,
        private cartService: CartService,
        private _configurationService: ConfigurationService,
        private _translateService: TranslateService,
        private authenticationService: AuthenticationService,
        private _genericModalService: GenericModalService,
        public _restApi: RestApiService,
        public _localizeRouterService: LocalizeRouterService,
        private router: Router
    ) {}

    public get user$(): Observable<boolean> {
        return this._$user.asObservable();
    }

    public get employees$(): Observable<boolean> {
        return this._$employees.asObservable();
    }

    public get showChoice$(): Observable<boolean> {
        return this._$showChoice.asObservable();
    }

    public get totalPrice$(): Observable<any> {
        return this._$totalPrice.asObservable();
    }

    public get showTotalPriceMessage$(): Observable<boolean> {
        return this._$showTotalPriceMessage.asObservable();
    }

    public get order$(): Observable<Order> {
        return this._$order.asObservable();
    }

    public get currencyPositionBefore$(): Observable<string> {
        return this._$currencyPositionBefore.asObservable();
    }

    public get errorMessage$(): Observable<boolean> {
        return this._$errorMessage.asObservable();
    }

    public get isGroupBooking$(): Observable<boolean> {
        return this._$isGroupBooking.asObservable();
    }

    public get addedParticipants$(): Observable<Array<Participant>> {
        return this._$addedParticipants.asObservable();
    }

    get f() {
        return this.booking.controls;
    }

    emitEventToChild() {
        this.eventsSubject.next();
    }

    ngOnInit() {
        window.scrollTo(0, 0);
        this.booking = this.formBuilder.group({
            note: [''],
            number: [''],
            persons: this.formBuilder.array([this.createPersonsGroup()])
        });

        this.orderService.order$.pipe(take(1)).subscribe((order) => {
            const persons = this.booking.get('persons') as UntypedFormArray;

            this.bookingPerson = JSON.stringify(order.user);
            if (!order.isGroupBooking) {
                if (order && order.participants) {
                    for (let i = 0; i < order.participants.length; i = i + 1) {
                        if (!persons.controls[i]) {
                            this.addParticipant(false);
                        }

                        persons.controls[i].get('person').setValue(order.participants[i].person);
                        persons.controls[i].get('sameAs').setValue(order.participants[i].sameAs);

                        this._configurationService.$siteSettings.subscribe((data) => {
                            if (data && data.length > 0 && JSON.stringify(this._currentData) !== JSON.stringify(data)) {
                                this._currentData = data;
                                const _countriesData = data.filter((f) => f.key.toLowerCase() === 'countries')[0];
                                const _regionData = data.filter((f) => f.key.toLowerCase() === 'regions')[0];
                                const _salutationsData = data.filter((f) => f.key.toLowerCase() === 'salutations')[0];

                                if (!order.participants[i].person.country && _countriesData && _countriesData.value) {
                                    order.participants[i].person.country = _countriesData.value[0];
                                }

                                if (!order.participants[i].person.salutation && _salutationsData && _salutationsData.value) {
                                    order.participants[i].person.salutation = _salutationsData.value[0];
                                }

                                if (_regionData && _regionData.value) {
                                    if (!order.participants[i].person.regionKey && _regionData && _regionData.value) {
                                        order.participants[i].person.regionKey = _regionData.value[0];
                                    }
                                }
                            }
                        });
                        if (i !== 0) {
                            if (order.participants[i].sameAs) {
                                order.participants[i].person.salutation = order.participants[0].person.salutation;
                                order.participants[i].person.company = order.participants[0].person.company;
                                order.participants[i].person.address = order.participants[0].person.address;
                                order.participants[i].person.city = order.participants[0].person.city;
                                order.participants[i].person.zipCode = order.participants[0].person.zipCode;
                                order.participants[i].person.regionKey = order.participants[0].person.regionKey;
                                order.participants[i].person.country = order.participants[0].person.country;
                                order.participants[i].person.houseNumber = order.participants[0].person.houseNumber;
                            }
                        }
                    }
                }
            }

            if (order.isGroupBooking && order.isGroupBooking !== null) {
                this.choice2 = true;
                this.choice1 = false;
                this._$isGroupBooking.next(true);
                this._$addedParticipants.next(order.participants);
            } else {
                this.choice1 = true;
                this.choice2 = false;
                this._$isGroupBooking.next(false);
            }

            this._$order.next(order);
            this.currency = this._configurationService.loadCurrenciesConfigurations(order.cartItems[0].currency);
            this.order = order;
        });

        if (this._configurationService.isPartnerPage()) {
            this._translateService.stream('common').subscribe((translation) => {
                this._$currencyPositionBefore.next(translation.currencyBeforePrice);
            });
        } else {
            this._configurationService.$siteSettings.subscribe((data) => {
                if (data && data.length > 0) {
                    this._$currencyPositionBefore.next(data.filter((f) => f.key.toLowerCase() === 'currency')[0].currencyBeforePrice);
                    this.format = data.filter((f) => f.key.toLowerCase() === 'currency')[0].format
                        ? data.filter((f) => f.key.toLowerCase() === 'currency')[0].format
                        : '';
                    this.languageCode = data.filter((f) => f.key.toLowerCase() === 'currency')[0].language
                        ? data.filter((f) => f.key.toLowerCase() === 'currency')[0].language
                        : '';
                }
            });
        }

        this._configurationService.$siteSettings.subscribe((data) => {
            if (data && data.length > 0 && JSON.stringify(this._currentData) !== JSON.stringify(data)) {
                this._currentData = data;
                const _countriesData = data.filter((f) => f.key.toLowerCase() === 'countries')[0];
                const _salutationsData = data.filter((f) => f.key.toLowerCase() === 'salutations')[0];
                const _regionsData = data.filter((f) => f.key.toLowerCase() === 'regions')[0];
                let region;
                if (_regionsData && _regionsData['value']) {
                    region = _regionsData['value'][0];
                } else {
                    region = '';
                }

                this.emptyPerson = {
                    address: '',
                    city: '',
                    company: '',
                    country: _countriesData['value'][0].code,
                    region: region,
                    department: '',
                    email: '',
                    firstName: '',
                    lastName: '',
                    phone: '',
                    salutation: _salutationsData['value'][0].code,
                    subscribe: false,
                    title: '',
                    zipCode: '',
                    companySameAs: false,
                    houseNumber: ''
                };
            }
        });
        const isPortal = this._configurationService.isPartnerPage();
        this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {
                if (!isPortal && this._configurationService.hasFeature('GroupBooking')) {
                    this.orderService.getEmployees().then((employees) => {
                        if (employees && employees.length > 0) {
                            this._$employees.next(employees);
                            this._$showChoice.next(true);
                        }
                    });
                } else if (isPortal) {
                    this._configurationService.getConfigPartnerByPortal().then((values) => {
                        if (values) {
                            const tempLanding = values.filter((setting) => setting.key === 'features');
                            let hasCheckPrerequest;
                            if (tempLanding && tempLanding[0] && tempLanding[0].value.length > 0) {
                                hasCheckPrerequest = tempLanding[0].value.filter((setting) => setting.name === 'GroupBooking');
                                this._$showChoice.next(hasCheckPrerequest[0].enabled);
                            }

                            if (hasCheckPrerequest && hasCheckPrerequest[0] && hasCheckPrerequest[0].value) {
                                this.orderService.getEmployees().then((employees) => {
                                    if (employees && employees.length > 0) {
                                        this._$employees.next(employees);
                                        this._$showChoice.next(true);
                                    }
                                });
                            }
                        }
                    });
                }
            }
        });
    }

    public isActive(currentStep: number) {
        if (currentStep < Number(this.activeStep)) {
            return true;
        }
    }

    public isCurrent(currentStep: number) {
        if (currentStep === Number(this.activeStep)) {
            return true;
        }
    }

    goToStep(stepNumber) {
        if (stepNumber === 3) {
            if (this.booking.invalid && !this.order.isGroupBooking) {
                this.emitEventToChild();
                this.order.participants = this.f.persons.value;
                this.orderService.updateOrder(this.order);
                this._$errorMessage.next(false);
                setTimeout(() => {
                    const errorMessage = document.querySelector('div.invalid-feedback') as HTMLElement;

                    if (errorMessage) {
                        errorMessage.offsetParent.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 400);
                return;
            } else {
                this._$errorMessage.next(false);

                if (!this.order.isGroupBooking) {
                    this.order.participants = this.f.persons.value;
                }
                if (this.order.participants && this.order.participants.length > 0) {
                    this.createScreeningObject();
                    this.requestForGTSScreening();
                    this.nextStep.emit(stepNumber);
                } else {
                    this._$errorMessage.next(true);
                }

                this.orderService.updateOrder(this.order);
            }
        } else {
            this.nextStep.emit(stepNumber);
            if (!this.order.isGroupBooking) {
                this.order.participants = this.f.persons.value;
            }
            this.orderService.updateOrder(this.order);
        }
    }

    addParticipant(showOverlay?: boolean) {
        const participants = this.booking.get('persons') as UntypedFormArray;

        if (participants.length === 7) {
            participants.push(this.createPersonsGroup());
            this.showErrorMessage = true;
        } else {
            participants.push(this.createPersonsGroup());
        }

        const persons = this.booking.get('persons') as UntypedFormArray;

        persons.controls[participants.length - 1].get('person').setValue(this.emptyPerson);
        persons.controls[participants.length - 1].get('sameAs').setValue(false);

        this._$order.subscribe((order) => {
            this.order = order;
            this.order.participants = this.f.persons.value;
            this.orderService.updateOrder(this.order);
            if (showOverlay) {
                this.openOverlay();
            }
        });
    }

    removeParticipant(index) {
        const participants = this.booking.get('persons') as UntypedFormArray;
        participants.removeAt(index);
        this.showErrorMessage = false;

        this.order.participants = this.f.persons.value;
        this.orderService.updateOrder(this.order);
        this.openOverlay();
    }

    sameAsPerson(index, event) {
        const persons = this.booking.get('persons') as UntypedFormArray;

        if (event) {
            this._order = this.orderService.order$.subscribe((order) => {
                if (persons.controls[index]) {
                    persons.controls[index].get('person').patchValue(order.user);
                }
            });
        } else {
            if (!this._configurationService.isPartnerPage()) {
                this._configurationService.$siteSettings.pipe(take(1)).subscribe((data) => {
                    if (data && data.length > 0) {
                        this.createEmptyPersion(data);
                        persons.controls[index].get('person').setValue(this.emptyPerson);
                    }
                });
            } else {
                this._configurationService.getConfigPartnerByPortal().then((values) => {
                    if (values) {
                        this.createEmptyPersion(values);
                        persons.controls[index].get('person').setValue(this.emptyPerson);
                    }
                });
            }
        }
    }

    createEmptyPersion(values) {
        this._currentData = values;
        const _salutationsData = values.filter((f) => f.key.toLowerCase() === 'salutations')[0];
        const _countriesData = values.filter((f) => f.key.toLowerCase() === 'countries')[0];
        const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
        let region;
        if (_regionsData && _regionsData['value']) {
            region = _regionsData['value'][0];
        } else {
            region = '';
        }
        this.emptyPerson = {
            address: '',
            city: '',
            company: '',
            country: _countriesData['value'][0].code,
            region: region,
            department: '',
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            salutation: _salutationsData['value'][0].code,
            subscribe: false,
            title: '',
            zipCode: '',
            companySameAs: false,
            houseNumber: ''
        };
    }
    // ngOnDestroy() {
    //     if (!this.order.isGroupBooking) {
    //         this.order.participants = this.f.persons.value;
    //     }
    //     this.orderService.updateOrder(this.order);
    // }

    public openOverlay(): void {
        this.orderService.order$.pipe(take(1)).subscribe((order) => {
            let totalPrice = 0;
            order.cartItems.forEach((element) => {
                this.authenticationService.loggedIn().subscribe((data) => {
                    if (data && element.externalPrice !== null) {
                        totalPrice += element.externalPrice as number;
                    } else {
                        totalPrice += element.coursePrice as number;
                    }
                });
            });

            this._$totalPrice.next(totalPrice * order.participants.length);
        });

        this._overlayHandler();
    }

    private createPersonsGroup(): UntypedFormGroup {
        return new UntypedFormGroup({
            person: new UntypedFormControl(''),
            sameAs: new UntypedFormControl('')
        });
    }

    private async _overlayHandler(): Promise<void> {
        this._$showTotalPriceMessage.next(true);
        setTimeout(() => this._$showTotalPriceMessage.next(false), 4000);
    }

    public goToCart() {
        this._$showTotalPriceMessage.next(false);
        this.cartService.activeStep(0);
    }

    public inputChanged(index) {
        const persons = this.booking.get('persons') as UntypedFormArray;

        if (persons.controls[index] && persons.controls[index].get('sameAs')) {
            this.orderService.order$.pipe(take(1)).subscribe((order) => {
                if (persons.controls[index]) {
                    persons.controls[index].get('sameAs').setValue(false);
                }
            });
        }
    }

    public choose1(event) {
        this.booking = this.formBuilder.group({
            note: [''],
            number: [''],
            persons: this.formBuilder.array([this.createPersonsGroup()])
        });

        this.choice1 = true;
        this.choice2 = false;
        this._$isGroupBooking.next(false);
        this.order.isGroupBooking = false;
        this.order.participants = [];
        this.orderService.updateOrder(this.order);

        this.orderService.order$.pipe(take(1)).subscribe((order) => {
            if (!order.isGroupBooking) {
                if (order) {
                    const newPerson = {
                        address: this.order.user.address,
                        city: this.order.user.city,
                        company: this.order.user.company,
                        country: this.order.user.country,
                        department: this.order.user.department,
                        email: this.order.user.email,
                        firstName: this.order.user.firstName,
                        lastName: this.order.user.lastName,
                        phone: this.order.user.phone,
                        salutation: this.order.user.salutation,
                        subscribe: this.order.user.subscribe,
                        title: this.order.user.title,
                        zipCode: this.order.user.zipCode,
                        houseNumber: this.order.user.houseNumber,
                        regionKey: this.order.user.regionKey
                    };

                    const persons = this.booking.get('persons') as UntypedFormArray;

                    persons.controls[0].get('person').setValue(newPerson);
                    persons.controls[0].get('sameAs').setValue(true);

                    const participantEmpty = {
                        person: newPerson,
                        sameAs: true
                    };

                    order.participants.push(participantEmpty);

                    this._configurationService.$siteSettings.subscribe((data) => {
                        if (data && data.length > 0 && JSON.stringify(this._currentData) !== JSON.stringify(data)) {
                            this._currentData = data;
                            const _salutationsData = data.filter((f) => f.key.toLowerCase() === 'salutations')[0];

                            if (!order.participants[0].person.salutation && _salutationsData && _salutationsData.value) {
                                order.participants[0].person.salutation = _salutationsData.value[0];
                            }
                        }
                    });
                }
            }

            this._$order.next(order);
            this.order = order;
        });
    }

    private _setEmptyParticipants(order: Order) {
        const _order = order;
        const participantEmpty = {
            person: this.emptyPerson,
            sameAs: true
        };

        _order.participants = [];
        _order.participants.push(participantEmpty);
        this._$order.next(_order);
    }

    public choose2(event) {
        this.choice1 = false;
        this.choice2 = true;
        this._$isGroupBooking.next(true);

        this.order.isGroupBooking = true;
        this.order.participants = [];
        this.orderService.updateOrder(this.order);
    }

    public openPopupEmployees() {
        this._modalId = this._genericModalService.create(this._goupEmployees);
        this._genericModalService.open(this._modalId, this._goupEmployees, true);
    }

    public closeModal() {
        this._genericModalService.close(this._modalId);
    }

    public addedParticipants(event) {
        if (event) {
            this.orderService.order$.pipe(take(1)).subscribe((order) => {
                this._$addedParticipants.next(order.participants);
                this._genericModalService.close(this._modalId);
                // this.closeModal();
                this.openOverlay();
                this._$errorMessage.next(false);
            });
        } else {
            this.closeModal();
        }
    }

    public removedParticipant(event) {
        this.orderService.order$.pipe(take(1)).subscribe((order) => {
            this._$addedParticipants.next(order.participants);
            this._genericModalService.close(this._modalId);
            // this.closeModal();
            this.openOverlay();
        });
    }

    public requestForGTSScreening() {
        const _url = `${environment.APIEndpoint}api/v1/booking/screening/gts`;

        return this._restApi.requestRawBodyPOST<any>(_url, JSON.stringify(this.screeningObject), null, true, true).then(
            (data) => {},
            (error) => {
                this.goToBookingErrorPage();
                console.log(error);
                return false;
            }
        );
    }
    public goToBookingErrorPage() {
        const isPortal = this._configurationService.isPartnerPage();
        if (isPortal) {
            const url = window.location.href;
            const arrayUrl = url.split('/');
            arrayUrl.pop();
            const newUrl = arrayUrl.join('/');
            window.location.href = newUrl + '/booking-error';
        } else {
            const translatedPath: any = this._localizeRouterService.translateRoute('/' + 'booking-error');
            this.router.navigate([translatedPath]);
        }
    }

    public createScreeningObject() {
        let timezone = 'unknown';
        try {
            timezone = /.*\s(.+)/.exec(new Date().toLocaleDateString(navigator.language, { timeZoneName: 'short' }))[1];
        } catch (e) {
            timezone = new Date().toTimeString().match(new RegExp('[A-Z](?!.*[(])', 'g')).join('');
        }
        const screeningParticipantArray = [];
        let screeningParticipant = {};
        for (const i of this.order.participants) {
            screeningParticipant = {
                firstName: i.person.firstName,
                lastName: i.person.lastName,
                companyName: i.person.company,
                city: i.person.city,
                street1: i.person.address,
                houseNumber: i.person.houseNumber,
                country: i.person.country,
                timeZone: timezone
            };
            screeningParticipantArray.push(screeningParticipant);
        }
        this.screeningObject = {
            Participants: screeningParticipantArray
        };
    }
}
